import { Button, Image } from 'antd';
import { FC, ReactNode, useEffect, useState } from 'react';
import { GroupConsumerProps, PreviewGroupPreview } from 'rc-image/lib/PreviewGroup';
import { ImagePreviewToolbar } from '../ImagePreviewToolbar';
import { ImagePreviewGlobalStyles } from './ImagePreviewGroup.styles';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

type Props = {
  items?: GroupConsumerProps['items'];
  preview?: PreviewGroupPreview;
  currentPreviewName?: string;
  onDelete?: () => void;
  children?: ReactNode;
  total: number;
};

export const ImagePreviewGroup: FC<Props> = ({ currentPreviewName, onDelete, preview, items, total, children }) => {
  const { title, current, onChange, ...previewParams } = preview;

  const [currentInternal, setCurrentInternal] = useState(current ?? 0);

  useEffect(() => {
    if (current !== undefined) {
      setCurrentInternal(current);
    }
  }, [current]);

  const handleChange = (nextIndex: number, prevIndex: number) => {
    setCurrentInternal(nextIndex);
    onChange?.(nextIndex, prevIndex);
  };

  return (
    <>
      <ImagePreviewGlobalStyles />
      <Image.PreviewGroup
        items={items}
        preview={{
          zIndex: 1202,
          imageRender: image => (
            <div className="image-group-preview">
              <div className="image-group-header">{title || <p className="image-title">{currentPreviewName}</p>}</div>
              <div className="image-container">
                {!!currentInternal && (
                  <Button
                    className="arrow-button arrow-left"
                    size="large"
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={() => handleChange(currentInternal - 1, currentInternal)}
                  />
                )}
                {image}
                {currentInternal < total - 1 && (
                  <Button
                    className="arrow-button arrow-right"
                    size="large"
                    type="text"
                    icon={<RightOutlined />}
                    onClick={() => handleChange(currentInternal + 1, currentInternal)}
                  />
                )}
              </div>
            </div>
          ),
          toolbarRender: (_, { image, ...params }) => (
            <ImagePreviewToolbar image={{ name: currentPreviewName, ...image }} onDelete={onDelete} {...params} />
          ),
          destroyOnClose: true,
          current: currentInternal,
          onChange: handleChange,
          ...previewParams,
        }}
      >
        {children}
      </Image.PreviewGroup>
    </>
  );
};
